import React, { FC, useCallback, useContext, useLayoutEffect, useState } from 'react';
import classNames from 'classnames';
import ConfirmationModalLayout from '../../shared/components/modal/ConfirmationModalLayout';
import AnalyseDropDown from '../analyse/AnalyseDropDown';
import { SelectionListDropDown } from '../../shared/common/SelectionListDropDown';
import SelectionBulkContainer from '../selectionlists/header/SelectionBulkContainer';
import SelectionBulkMenu from '../../shared/components/SelectionBulkMenu';
import TotalDropDown from '../selectionlists/TotalDropDown';
import TotalAmountBadges from '../../shared/common/TotalAmountBadges';
import { useIsAdmin, useIsCustomerAllowedToOrder, useIsUserAllowedToOrder } from '../../shared/utils';
import { SelectionList } from '../../../models/SelectionList';
import { PriceTier } from '../../../models/PriceTier';
import { Button, ButtonSize, ButtonTheme } from '../../shared/common/Button';
import { useCartSummaryQuery } from '../../api/shopping-cart/useCartSummaryQuery';
import './CartHeader.scss';
import { DropDownMenu } from '../../shared/common/DropDownMenu';
import { AnalyseResponseOut, BulkActionResponse, BulkAddProductsToSelectionListResponse } from '../../store/api';
import { ImportIsbnDialog } from '../selectionlists/header/ImportIsbnDialog';
import { SubscriptionPeriodType } from '../../../models/Article';
import { DropdownContext } from '../../hooks/dropdownContext';
import { useTranslation } from 'react-i18next';
import { fetchShoppingCartAnalyse } from '../../api/shopping-cart/fetchAnalyse';
import AnalyseTable from '../analyse/AnalyseTable';
import { t } from 'i18next';
import {
    CopyToListOptions,
    IsbnAddToCartOptions,
    isCopyToListOptions,
    isIsbnAddToCartOptions,
    isMoveOptions,
    MoveToListOptions
} from '../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../shared/common/CheckBeforeAddingModal';
import {
    ACTION_ADD,
    ACTION_COPY,
    ACTION_DECREASE,
    ACTION_INCREASE,
    ACTION_MOVE,
    ACTION_SET
} from '../../store/constants';
import { RemoveUnavailableProductsModal } from '../../shared/common/RemoveUnavailableProductsModal';
import { useUnavailableProductsCheck } from '../../shared/common/useUnavailableProductsCheck';
import { fetchUnavailableProducts } from './fetchUnavailableProducts';
import { UseMutationResult } from '@tanstack/react-query';
import { ErrorResponse, TYPE_ERROR } from '../../shared/notification/actions';
import { useAddProductsByIsbnsToCart } from '../../api/shopping-cart/useAddProductsByIsbnsToCart';
import { UnavailableProductsModal } from '../../shared/common/UnavailableProductsModal';
import { useSelectionLists } from '../../api/selection-list/useSelectionLists';
import { useStickyHeader } from '../../shared/header/useStickyHeader';
import { showNotification } from '../../shared/notification/toastNotification';
import { useNavigate } from 'react-router-dom';

type Props = {
    readonly isInSelectionMode?: boolean;
    readonly totalProducts?: number;
    readonly cancelSelectionMode: () => void;
    readonly onDownloadCSV: () => Promise<void>;
    readonly onDelete: () => void;
    readonly onCreateNewListClicked: (name: string) => void;
    readonly onCopyToList: (
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onMoveToList: (
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onCopyAmountChange: (
        amount: number,
        command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE
    ) => Promise<BulkActionResponse>;
    readonly onRemoveFromCart: () => void;
    readonly onMoveCartToList: (
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onCopyCartToList: (
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onChangePrice: (priceTier: PriceTier, performChecks: boolean) => Promise<BulkActionResponse>;
    readonly bulkChangeSubscriptionPeriodMutation: UseMutationResult<void, ErrorResponse, SubscriptionPeriodType>;
    readonly onBulkAddToGroup: (productGroupId: number) => void;
    readonly productSelectionCount: number;
    readonly isNothingSelected: boolean;
};

export const CartHeader: FC<Props> = props => {
    const { data: cartSummary } = useCartSummaryQuery();
    const { t } = useTranslation();
    const { isSticky } = useStickyHeader();

    return (
        <>
            <SelectionBulkContainer
                movedUp={!!props.isInSelectionMode}
                isSticky={isSticky}
                headerComponent={
                    <CartHeaderHeader
                        onMoveCartToList={props.onMoveCartToList}
                        onCreateNewListClicked={props.onCreateNewListClicked}
                        onDownloadCSV={props.onDownloadCSV}
                        onCopyCartToList={props.onCopyCartToList}
                        onDelete={props.onDelete}
                        totalProducts={props.totalProducts}
                        isInSelectionMode={props.isInSelectionMode}
                    />
                }
                bulkMenuComponent={
                    <SelectionBulkMenu
                        isInSelectionMode={props.isInSelectionMode}
                        cancelSelection={props.cancelSelectionMode}
                        selectedCount={props.productSelectionCount}
                        onCopyToList={props.onCopyToList}
                        onMoveToList={props.onMoveToList}
                        onCopyAmountChange={props.onCopyAmountChange}
                        onRemoveFrom={props.onRemoveFromCart}
                        deleteLabel={t('removeFromCart')}
                        isMoveToListEnabled={true}
                        isCopyToListEnabled={true}
                        isChangeQuantityEnabled={true}
                        isAddToCartEnabled={false}
                        isDeleteFromEnabled={true}
                        isAddToProductGroupEnabled={true}
                        isChangeSubscriptionPeriodEnabled={(cartSummary.epressAmount ?? 0) > 0}
                        onBulkAddToGroup={props.onBulkAddToGroup}
                        onChangePrice={props.onChangePrice}
                        bulkChangeSubscriptionPeriodMutation={props.bulkChangeSubscriptionPeriodMutation}
                        isNothingSelected={props.isNothingSelected}
                    />
                }
            />
        </>
    );
};

type CartHeaderHeaderProps = {
    readonly isInSelectionMode?: boolean;
    readonly totalProducts?: number;
    readonly onDownloadCSV: () => Promise<void>;
    readonly onCreateNewListClicked: (name: string) => void;
    readonly onMoveCartToList: (
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onCopyCartToList: (
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onDelete: () => void;
};

const CartHeaderHeader: FC<CartHeaderHeaderProps> = ({
    isInSelectionMode,
    totalProducts,
    onDownloadCSV,
    onCreateNewListClicked,
    onMoveCartToList,
    onCopyCartToList,
    onDelete
}) => {
    const { t } = useTranslation();
    const { data: cartSummary, status } = useCartSummaryQuery();

    const [analyse, setAnalyse] = useState<AnalyseResponseOut | undefined>(undefined);

    const [isAnalyseOpen, setIsAnalyseOpen] = useState(false);
    const [isAnalyseLoading, setIsAnalyseLoading] = useState(true);

    const onAnalyseClicked = useCallback(() => setIsAnalyseOpen(prevState => !prevState), []);

    useLayoutEffect(() => {
        const fetchAnalyse = async () => {
            if (isAnalyseOpen) {
                try {
                    setIsAnalyseLoading(true);
                    const res = await fetchShoppingCartAnalyse();
                    setAnalyse(res);
                } catch (e) {
                    setAnalyse(undefined);
                } finally {
                    setIsAnalyseLoading(false);
                }
            }
        };

        fetchAnalyse().then();
    }, [isAnalyseOpen]);

    if (status !== 'success') {
        return null;
    }

    const selectionListHeadlineClasses = classNames('selection-detail-list cart-detail-list', {
        'moved-up': isInSelectionMode
    });

    return (
        <div className={'cart-header ' + selectionListHeadlineClasses}>
            <i className="cart-icon material-icons">shopping_cart</i>
            <span className="cart-label">{t('cart.cart')}</span>
            <CartDropDown
                onCopyCartToList={onCopyCartToList}
                onCreateNewListClicked={onCreateNewListClicked}
                onMoveCartToList={onMoveCartToList}
                onDelete={onDelete}
            />
            <AnalyseDropDown
                analysis={analyse}
                tableComponent={AnalyseTable}
                onAnalyseClicked={onAnalyseClicked}
                label={t('cart.analyseCart')}
                showDropdown={isAnalyseOpen}
                isLoading={isAnalyseLoading}
            />
            <div style={{ flexGrow: 1 }} />
            <TotalAmountBadges
                currency={cartSummary.currency}
                eaudiobooksAmount={cartSummary.eaudiobooksAmount}
                eaudiobooksValue={cartSummary.eaudiobooksValue}
                ebooksAmount={cartSummary.ebooksAmount}
                ebooksValue={cartSummary.ebooksValue}
                epressAmount={cartSummary.epressAmount}
                epressValue={cartSummary.epressValue}
            />
            <TotalDropDown summary={cartSummary} textTotalLine={t('cart.cartTotal')} />
            {!!totalProducts && totalProducts > 0 && <DownloadButton onDownloadCSV={onDownloadCSV} />}
            <CheckoutButton totalProducts={totalProducts} />
        </div>
    );
};

type CartDropDownProps = {
    readonly onCreateNewListClicked: (name: string) => void;
    readonly onMoveCartToList: (
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onCopyCartToList: (
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onDelete: () => void;
};

const CartDropDown: FC<CartDropDownProps> = props => {
    const { t } = useTranslation();
    const { isExpandedContext } = useContext(DropdownContext);
    const { selectionLists } = useSelectionLists();
    const [dropDownActive, setDropDownActive] = useState(false);
    const [showDeleteCartConfirmation, setShowDeleteCartConfirmation] = useState(false);
    const [showIsbnDialog, setShowIsbnDialog] = useState(false);
    const ddClasses = classNames('cart-drop-down default-dd-nav dd-w-select expanded-dd list-actions fl-none', {
        active: dropDownActive
    });

    const { isLoading, addByIsbnsToCartBulk } = useAddProductsByIsbnsToCart();
    const isAdminRole = useIsAdmin();

    const onAction = useCallback(
        (performChecks: boolean, options: CopyToListOptions | MoveToListOptions | IsbnAddToCartOptions) => {
            if (isIsbnAddToCartOptions(options)) {
                return addByIsbnsToCartBulk({ isbns: options.isbns, performChecks });
            }

            if (isCopyToListOptions(options)) {
                return props.onCopyCartToList(options.list, performChecks);
            }

            if (isMoveOptions(options)) {
                return props.onMoveCartToList(options.list, performChecks);
            }
            throw new Error('technical error');
        },
        [addByIsbnsToCartBulk, props]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } = useListContentChecks<
        CopyToListOptions | MoveToListOptions | IsbnAddToCartOptions
    >({
        onAction
    });

    const checkBeforeAddingModalProps = getCheckBeforeAddingModalProps();

    const handleListClick = useCallback(
        async (list: SelectionList, action: typeof ACTION_COPY | typeof ACTION_MOVE) => {
            try {
                await changePrice({ target: ListActionTarget.List, list, action });
            } finally {
                setDropDownActive(a => !a);
            }
        },
        [changePrice]
    );

    const onBulkImportISBNs = useCallback(
        async (isbns: string) => {
            try {
                await changePrice({ target: ListActionTarget.Cart, isbns, action: ACTION_ADD });
            } finally {
                setShowIsbnDialog(false);
            }
        },
        [changePrice]
    );

    return (
        <>
            <ConfirmationModalLayout
                visible={showDeleteCartConfirmation}
                title={t('cart.delete')}
                text={t('cart.deleteMessage')}
                onConfirm={() => {
                    setShowDeleteCartConfirmation(false);
                    setDropDownActive(false);
                    props.onDelete();
                }}
                onCancel={() => setShowDeleteCartConfirmation(false)}
            />

            <ImportIsbnDialog
                onCancel={() => setShowIsbnDialog(false)}
                onImport={onBulkImportISBNs}
                show={showIsbnDialog}
                isLoading={isLoading}
            />

            <DropDownMenu
                className={ddClasses}
                arrowIcon="more_horiz"
                expanded={dropDownActive}
                onToggleClick={setDropDownActive}
                onClickOutside={isExpandedContext ? () => {} : setDropDownActive}
                displacement={48}
            >
                <li>
                    <SelectionListDropDown
                        label={t('cart.moveCartToList')}
                        iconCode="move-product"
                        lists={selectionLists}
                        onCreateNewListClicked={props.onCreateNewListClicked}
                        onListClick={list => handleListClick(list, ACTION_MOVE)}
                        disabled={checkBeforeAddingModalProps.isLoading}
                    />
                </li>
                <li>
                    <SelectionListDropDown
                        label={t('cart.copyCartToList')}
                        iconCode="copy-product"
                        lists={selectionLists}
                        onCreateNewListClicked={props.onCreateNewListClicked}
                        onListClick={list => handleListClick(list, ACTION_COPY)}
                        disabled={checkBeforeAddingModalProps.isLoading}
                    />
                </li>
                <li>
                    <div className="default-dd-nav bt-default fl-left">
                        <a onClick={() => setShowDeleteCartConfirmation(true)} className="display-block">
                            <i className="material-icons normal fl-left">delete</i> {t('cart.clear')}
                        </a>
                    </div>
                </li>
                {isAdminRole ? (
                    <li>
                        <div className="default-dd-nav bt-default fl-left">
                            <a onClick={() => setShowIsbnDialog(true)} className="display-block isbn-import">
                                <i className="bolinda-icons fl-left">bolinda_only</i> {t('isbn.importIsbns')}
                            </a>
                        </div>
                    </li>
                ) : null}
            </DropDownMenu>
            <CheckBeforeAddingModal {...checkBeforeAddingModalProps} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </>
    );
};

type CheckoutButtonProps = {
    readonly totalProducts?: number;
};
const CheckoutButton: FC<CheckoutButtonProps> = ({ totalProducts }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data: cartSummary, status: cartSummaryQueryStatus } = useCartSummaryQuery();
    const isUserAllowedToOrder = useIsUserAllowedToOrder();
    const isCustomerAllowedToOrder = useIsCustomerAllowedToOrder();
    const { triggerUnavailableProductsCheck, getRemoveUnavailableProductsModalProps } = useUnavailableProductsCheck({
        onFetchUnavailableProducts: fetchUnavailableProducts
    });

    const removeUnavailableProductsModalProps = getRemoveUnavailableProductsModalProps();

    if (cartSummaryQueryStatus !== 'success') {
        return null;
    }

    if (!totalProducts) {
        return (
            <Button
                text={t('reserveReports.reserveReport.viewReport.proceedToCheckOut')}
                iconRight="arrow_forward"
                size={ButtonSize.LARGE}
                theme={ButtonTheme.NEUTRAL}
                onClick={() => {}}
                disabled={true}
            />
        );
    }

    if (!isCustomerAllowedToOrder) {
        return (
            <Button
                text={t('reserveReports.reserveReport.viewReport.proceedToCheckOut')}
                iconRight="arrow_forward"
                size={ButtonSize.LARGE}
                onClick={() => showNotification(t('notifications.showOrdersNotAllowedNotification.error'), TYPE_ERROR)}
            />
        );
    }

    if (isUserAllowedToOrder) {
        if (cartSummary.comingSoonProductsAmount > 0 || cartSummary.unavailableProductsAmount > 0) {
            return (
                <>
                    <Button
                        text={t('reserveReports.reserveReport.viewReport.proceedToCheckOut')}
                        iconRight="arrow_forward"
                        size={ButtonSize.LARGE}
                        onClick={triggerUnavailableProductsCheck}
                        disabled={removeUnavailableProductsModalProps.isLoading}
                        isLoading={removeUnavailableProductsModalProps.isLoading}
                    />
                    <RemoveUnavailableProductsModal {...removeUnavailableProductsModalProps} />
                </>
            );
        } else {
            return (
                <Button
                    text={t('reserveReports.reserveReport.viewReport.proceedToCheckOut')}
                    iconRight="arrow_forward"
                    size={ButtonSize.LARGE}
                    onClick={() => navigate('/checkout')}
                />
            );
        }
    }

    return (
        <Button
            text={t('reserveReports.reserveReport.viewReport.proceedToCheckOut')}
            iconRight="arrow_forward"
            size={ButtonSize.LARGE}
            theme={ButtonTheme.NEUTRAL}
            onClick={() => {}}
            disabled={true}
        />
    );
};

type DownloadButtonProps = {
    readonly onDownloadCSV: () => Promise<void>;
};
const DownloadButton: FC<DownloadButtonProps> = ({ onDownloadCSV }) => {
    const isAdmin = useIsAdmin();

    if (!isAdmin) {
        return null;
    }

    return <Button text={t('downloadCsv')} size={ButtonSize.LARGE} icon="bolinda_only" onClick={onDownloadCSV} />;
};
